/**
 * Dedicated Impound History page.
 *
 * Shows closed impound records (released to driver / released to NTS /
 * archived) separately from the regular Jobs history. Used by:
 *  • Dispatcher / Admin — quick lookup of past releases for reprinting receipts
 *    or auditing payments
 *  • billing user (viewer) — read-only paper-trail with full detail sheet access
 *    (matches the printed receipt) and ability to archive once paid/written off
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Search, X, Archive, History as HistoryIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { listImpounds, archiveJob } from "../lib/api";
import { useAuth } from "../lib/auth";
import { cn, formatTimeAgo } from "../lib/utils";
import { toast } from "sonner";
import ImpoundDetailSheet from "../components/ImpoundDetailSheet";
import TowAuthorityCell from "../components/TowAuthorityCell";
import { salvageFull, salvageShort, tenantLocale, tenantTz } from "../lib/company";
import { hasCap, can } from "../lib/roles";

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" }) : "—";

function deriveStatus(j) {
  if (j.archived_at) return { label: "Archived", cls: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" };
  if (j.released_to_nts_at) return { label: `To ${salvageShort()}`, cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" };
  if (j.released_at) return { label: "Released", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" };
  return { label: "In yard", cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200" };
}

const POLL_MS = 30000;

/** Returns "closed at" date for any non-active row (released / NTS / archived). */
function closedAt(j) {
  return j.released_at || j.released_to_nts_at || j.archived_at || j.created_at;
}

export default function ImpoundHistoryPage() {
  const { user } = useAuth();
  const role = user?.role;
  const canArchive = hasCap(user, "admin", "dispatcher") && can(user, "archive_jobs");
  // Same back-office trio can edit the Tow Authority # here — useful when
  // fixing a missing/incorrect number while invoicing post-release.
  const canSetTowAuthority = hasCap(user, "admin", "dispatcher");

  // 'history' = released + nts + archived (all closed). Sub-filter narrows further.
  const [filter, setFilter] = useState("history");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailJob, setDetailJob] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  // Stale-response guard (Aug 2026 sweep) — prevents overlapping fetches
  // from clobbering fresh results with older, broader data.
  const reqIdRef = useRef(0);

  const refresh = useCallback(async (showSpinner = false) => {
    const myReqId = ++reqIdRef.current;
    if (showSpinner) setLoading(true);
    try {
      // Fetch enough data, then filter client-side for the combined 'history' view.
      const params = { status: filter === "history" ? "all" : filter };
      if (query.trim()) params.q = query.trim();
      const all = await listImpounds(params);
      if (myReqId !== reqIdRef.current) return;
      // Archiving is how the office gets a row off the list, so an archived
      // impound stays out of "All closed" and is only reachable by picking
      // Archived — otherwise the list only ever grows.
      const closedOnly = filter === "history"
        ? all.filter((j) => !j.archived_at && (j.released_at || j.released_to_nts_at))
        : all;
      // Sort by date closed, newest first.
      closedOnly.sort((a, b) => new Date(closedAt(b)) - new Date(closedAt(a)));
      setRows(closedOnly);
    } catch {
      if (myReqId !== reqIdRef.current) return;
      toast.error("Failed to load impound history");
    } finally {
      // Clear on the LATEST request regardless of who set it: a silent
      // refetch overtaking a spinner request would otherwise leave the
      // spinner up forever (seen on Storage Yards, Oct 2026).
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [filter, query]);

  useEffect(() => {
    const id = setTimeout(() => refresh(true), 250);
    return () => clearTimeout(id);
  }, [refresh]);

  // Live polling so the billing user stays in sync without manual refresh.
  useEffect(() => {
    const t = setInterval(() => refresh(false), POLL_MS);
    const onFocus = () => refresh(false);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [refresh]);

  const onArchive = async () => {
    if (!archiveTarget) return;
    try {
      await archiveJob(archiveTarget.id);
      toast.success("Impound archived");
      setArchiveTarget(null);
      refresh(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="impound-history-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <HistoryIcon className="h-7 w-7 text-blue-700 dark:text-blue-300" />
            Impound History
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Past impound releases — click any row to view or re-print the receipt details
          </p>
        </div>
      </div>

      {/* Filters bar */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search rego, vehicle, colour, owner, phone, notice #, claim #, address…"
            className="w-full pl-9 pr-9 py-2 bg-slate-50 dark:bg-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="impound-history-search"
          />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]" data-testid="impound-history-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="history">All closed (default)</SelectItem>
            <SelectItem value="released">Released to owner</SelectItem>
            <SelectItem value="released_to_nts">Signed Over</SelectItem>
            <SelectItem value="archived">Archived (hidden from All closed)</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {loading ? "Loading…" : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <tr>
              <th className="text-left px-3 py-2 font-bold">Date Closed</th>
              <th className="text-left px-3 py-2 font-bold">Rego / Vehicle</th>
              <th className="text-left px-3 py-2 font-bold">Driver</th>
              <th className="text-left px-3 py-2 font-bold">Impounded</th>
              <th className="text-right px-3 py-2 font-bold whitespace-nowrap">Amount</th>
              <th className="text-left px-3 py-2 font-bold">Notice #</th>
              {canSetTowAuthority && <th className="text-left px-3 py-2 font-bold whitespace-nowrap">Tow Authority #</th>}
              <th className="text-left px-3 py-2 font-bold">Outcome</th>
              {canArchive && <th className="px-3 py-2"></th>}
            </tr>
          </thead>
          <tbody data-testid="impound-history-rows">
            {loading && <tr><td colSpan={(canSetTowAuthority ? 1 : 0) + (canArchive ? 8 : 7)} className="text-center text-slate-500 py-12">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={(canSetTowAuthority ? 1 : 0) + (canArchive ? 8 : 7)} className="text-center text-slate-400 py-12">No history yet.</td></tr>
            )}
            {!loading && rows.map((j) => {
              const s = deriveStatus(j);
              const closedDate = closedAt(j);
              const amount = j.payment_amount ?? j.released_to_nts_amount_owed ?? j.pricing_breakdown?.total;
              return (
                <tr
                  key={j.id}
                  onClick={() => setDetailJob(j)}
                  className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                  data-testid={`impound-history-row-${j.id}`}
                  title="Click for full impound details"
                >
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    <div className="font-semibold">{fmtDate(closedDate)}</div>
                    <div className="text-xs text-slate-400">{formatTimeAgo(closedDate)}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-900 dark:text-slate-100 font-mono">{j.rego || "—"}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{j.vehicle}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                    <div>{j.customer_name || j.impound_owner_name || "—"}</div>
                    <div className="text-xs text-slate-500">{j.customer_phone || j.impound_owner_phone}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    <div>{fmtDate(j.impounded_at)}</div>
                    <div className="text-xs text-slate-400">{j.impound_term === "28d" ? "28 days" : j.impound_term === "6mo" ? "6 months" : "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-900 dark:text-slate-100 font-mono font-semibold whitespace-nowrap">{fmtMoney(amount)}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400 text-xs font-mono">
                    <div>{j.impound_notice_number || j.police_reference || "—"}</div>
                  </td>
                  {canSetTowAuthority && (
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <TowAuthorityCell
                        job={j}
                        disabled={!!j.archived_at}
                        testIdPrefix="impound-history-tow-authority"
                        onSaved={(updated) =>
                          setRows((rs) => rs.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)))
                        }
                      />
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <span className={cn("text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded", s.cls)}>{s.label}</span>
                  </td>
                  {canArchive && (
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {!j.archived_at && (j.released_at || j.released_to_nts_at) && (
                        <Button
                          size="sm"
                          onClick={() => setArchiveTarget(j)}
                          className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white h-8 px-2.5 text-xs"
                          data-testid={`impound-history-archive-${j.id}`}
                        >
                          <Archive className="h-3 w-3" />
                          Archive
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ImpoundDetailSheet
        job={detailJob}
        open={!!detailJob}
        onOpenChange={(o) => !o && setDetailJob(null)}
        role={role}
        onArchive={(j) => setArchiveTarget(j)}
      />

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="impound-history-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this impound?</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveTarget?.rego || archiveTarget?.vehicle} will be hidden from the default view. All data + receipt are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="impound-history-archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onArchive} data-testid="impound-history-archive-confirm-btn">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
